import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Menu, X, LogOut, Phone, User, Settings, Bell, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { userAPI } from '../services/apiService';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLoginDropdown, setShowLoginDropdown] = useState(false);
  const notificationRef = useRef(null);
  const loginDropdownRef = useRef(null);

  useEffect(() => {
    const authStatus = localStorage.getItem('isAuthenticated');
    const userData = localStorage.getItem('user');
    
    if (authStatus === 'true' && userData) {
      setIsAuthenticated(true);
      setUser(JSON.parse(userData));
      loadNotifications();
    }
  }, [location]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (loginDropdownRef.current && !loginDropdownRef.current.contains(event.target)) {
        setShowLoginDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(() => {
        loadNotifications(true); // Silent reload
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const loadNotifications = async (silent = false) => {
    try {
      const data = await userAPI.getNotifications();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch (error) {
      if (!silent) {
        console.error('Error loading notifications:', error);
      }
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read if unread
      if (!notification.isRead) {
        await userAPI.markNotificationRead(notification.id);
        await loadNotifications(true);
      }

      // Navigate to link if provided
      if (notification.link) {
        if (notification.link.startsWith('http')) {
          window.open(notification.link, '_blank');
        } else {
          navigate(notification.link);
          setShowNotifications(false);
        }
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const toggleReadStatus = async (e, notification) => {
    e.stopPropagation();
    try {
      if (notification.isRead) {
        await userAPI.markNotificationUnread(notification.id);
      } else {
        await userAPI.markNotificationRead(notification.id);
      }
      await loadNotifications(true);
      toast.success(notification.isRead ? 'Marked as unread' : 'Marked as read');
    } catch (error) {
      console.error('Error toggling notification status:', error);
      toast.error('Failed to update notification');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
    toast.success('Logged out successfully');
    navigate('/');
  };

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50 border-b-4 border-red-400">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img 
              src="/netix-logo-main.png" 
              alt="Netix Fibernet" 
              className="h-12"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated ? (
              <>
                <Link 
                  to="/dashboard" 
                  className="px-4 py-2 rounded-lg border-2 border-red-300 bg-white hover:bg-red-50 text-gray-800 font-semibold transition-all hover:border-red-400 shadow-sm"
                >
                  Dashboard
                </Link>
                <Link 
                  to="/place-order" 
                  className="px-4 py-2 rounded-lg border-2 border-yellow-300 bg-white hover:bg-yellow-50 text-gray-800 font-semibold transition-all hover:border-yellow-400 shadow-sm"
                >
                  Place Order
                </Link>
                <Link 
                  to="/referral-links" 
                  className="px-4 py-2 rounded-lg border-2 border-pink-300 bg-white hover:bg-pink-50 text-gray-800 font-semibold transition-all hover:border-pink-400 shadow-sm"
                >
                  Referral Links
                </Link>
                <Link 
                  to="/transactions" 
                  className="px-4 py-2 rounded-lg border-2 border-red-300 bg-white hover:bg-red-50 text-gray-800 font-semibold transition-all hover:border-red-400 shadow-sm"
                >
                  Transactions
                </Link>
                <Link 
                  to="/messages" 
                  className="px-4 py-2 rounded-lg border-2 border-yellow-300 bg-white hover:bg-yellow-50 text-gray-800 font-semibold transition-all hover:border-yellow-400 shadow-sm"
                >
                  Messages
                </Link>
                <Link 
                  to="/profile" 
                  className="flex items-center gap-1 px-4 py-2 rounded-lg border-2 border-pink-300 bg-white hover:bg-pink-50 text-gray-800 font-semibold transition-all hover:border-pink-400 shadow-sm"
                >
                  <Settings className="h-4 w-4" />
                  Settings
                </Link>
                
                {/* Notification Bell */}
                <div className="relative" ref={notificationRef}>
                  <button
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <Bell className="h-5 w-5 text-gray-700" />
                    {unreadCount > 0 && (
                      <span className="absolute top-0 right-0 h-5 w-5 bg-red-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {/* Notification Dropdown */}
                  {showNotifications && (
                    <div className="absolute right-0 mt-2 w-96 bg-white border-2 border-gray-200 rounded-lg shadow-2xl z-50 max-h-96 overflow-y-auto">
                      <div className="p-4 border-b-2 border-gray-200 bg-gradient-to-r from-red-50 to-yellow-50">
                        <h3 className="font-bold text-gray-900 text-lg">Notifications</h3>
                        <p className="text-sm text-gray-600">{unreadCount} unread</p>
                      </div>

                      {notifications.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          <Bell className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                          <p>No notifications yet</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-200">
                          {notifications.map((notif) => (
                            <div
                              key={notif.id}
                              onClick={() => handleNotificationClick(notif)}
                              className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                                !notif.isRead ? 'bg-blue-50' : ''
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <Bell className={`h-5 w-5 mt-1 flex-shrink-0 ${notif.isRead ? 'text-gray-400' : 'text-blue-600'}`} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <p className={`font-semibold text-sm ${notif.isRead ? 'text-gray-700' : 'text-gray-900'}`}>
                                      {notif.title}
                                    </p>
                                    <button
                                      onClick={(e) => toggleReadStatus(e, notif)}
                                      className="text-xs px-2 py-1 rounded hover:bg-gray-200 transition-colors flex-shrink-0"
                                    >
                                      {notif.isRead ? 'Mark Unread' : 'Mark Read'}
                                    </button>
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{notif.message}</p>
                                  {notif.link && (
                                    <div className="flex items-center gap-1 text-xs text-blue-600 mt-2">
                                      <ExternalLink className="h-3 w-3" />
                                      <span>View More</span>
                                    </div>
                                  )}
                                  <p className="text-xs text-gray-500 mt-2">{formatDate(notif.createdAt)}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-4">
                  <span className="text-gray-700 font-medium">Hi, {user?.firstName}</span>
                  <Button 
                    onClick={handleLogout}
                    variant="outline"
                    className="border-2 border-gray-300 hover:border-red-500 hover:text-red-600"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Eye-Catching Gradient Navigation Buttons with 3D Glow */}
                <Link 
                  to="/" 
                  className="relative px-4 py-2.5 rounded-xl text-sm bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold transition-all duration-300 shadow-[0_4px_20px_rgba(124,58,237,0.5)] hover:shadow-[0_6px_30px_rgba(124,58,237,0.7)] hover:-translate-y-1 hover:scale-105"
                >
                  Home
                </Link>
                <Link 
                  to="/about" 
                  className="relative px-4 py-2.5 rounded-xl text-sm bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold transition-all duration-300 shadow-[0_4px_20px_rgba(124,58,237,0.5)] hover:shadow-[0_6px_30px_rgba(124,58,237,0.7)] hover:-translate-y-1 hover:scale-105"
                >
                  About
                </Link>
                <Link 
                  to="/coverage-area" 
                  className="relative px-4 py-2.5 rounded-xl text-sm bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold transition-all duration-300 shadow-[0_4px_20px_rgba(124,58,237,0.5)] hover:shadow-[0_6px_30px_rgba(124,58,237,0.7)] hover:-translate-y-1 hover:scale-105"
                >
                  Coverage Area
                </Link>
                <Link 
                  to="/speed-test" 
                  className="relative px-4 py-2.5 rounded-xl text-sm bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold transition-all duration-300 shadow-[0_4px_20px_rgba(124,58,237,0.5)] hover:shadow-[0_6px_30px_rgba(124,58,237,0.7)] hover:-translate-y-1 hover:scale-105"
                >
                  Speed Test
                </Link>
                <Link 
                  to="/make-payment" 
                  className="relative px-4 py-2.5 rounded-xl text-sm bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold transition-all duration-300 shadow-[0_4px_20px_rgba(124,58,237,0.5)] hover:shadow-[0_6px_30px_rgba(124,58,237,0.7)] hover:-translate-y-1 hover:scale-105"
                >
                  Payment
                </Link>
                <Link 
                  to="/request-service" 
                  className="relative px-4 py-2.5 rounded-xl text-sm bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold transition-all duration-300 shadow-[0_4px_20px_rgba(124,58,237,0.5)] hover:shadow-[0_6px_30px_rgba(124,58,237,0.7)] hover:-translate-y-1 hover:scale-105"
                >
                  Order Services
                </Link>
                <Link 
                  to="/join-our-team" 
                  className="relative px-4 py-2.5 rounded-xl text-sm bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold transition-all duration-300 shadow-[0_4px_20px_rgba(124,58,237,0.5)] hover:shadow-[0_6px_30px_rgba(124,58,237,0.7)] hover:-translate-y-1 hover:scale-105"
                >
                  Join Us
                </Link>
                <Link 
                  to="/career" 
                  className="relative px-4 py-2.5 rounded-xl text-sm bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold transition-all duration-300 shadow-[0_4px_20px_rgba(124,58,237,0.5)] hover:shadow-[0_6px_30px_rgba(124,58,237,0.7)] hover:-translate-y-1 hover:scale-105"
                >
                  Career
                </Link>
                
                {/* Login Dropdown */}
                <div className="relative" ref={loginDropdownRef}>
                  <button
                    onClick={() => setShowLoginDropdown(!showLoginDropdown)}
                    className="relative px-4 py-2.5 rounded-xl text-sm bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold transition-all duration-300 shadow-[0_4px_20px_rgba(124,58,237,0.5)] hover:shadow-[0_6px_30px_rgba(124,58,237,0.7)] hover:-translate-y-1 hover:scale-105"
                  >
                    Login
                  </button>
                  
                  {showLoginDropdown && (
                    <div className="absolute right-0 mt-2 w-56 bg-white border-2 border-violet-200 rounded-xl shadow-2xl z-50">
                      <div className="p-2">
                        <button
                          onClick={() => {
                            navigate('/customer-portal/login');
                            setShowLoginDropdown(false);
                          }}
                          data-testid="nav-customer-portal-link"
                          className="w-full text-left px-4 py-3 rounded-lg hover:bg-violet-50 transition-colors text-gray-800 font-semibold flex items-center gap-2"
                        >
                          <User className="h-5 w-5 text-violet-600" />
                          Customer Portal
                        </button>
                        <button
                          onClick={() => {
                            navigate('/Customer-dashboard/login');
                            setShowLoginDropdown(false);
                          }}
                          data-testid="nav-admin-portal-link"
                          className="w-full text-left px-4 py-3 rounded-lg hover:bg-violet-50 transition-colors text-gray-800 font-semibold flex items-center gap-2"
                        >
                          <User className="h-5 w-5 text-violet-600" />
                          Admin Portal
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                <button 
                  onClick={() => navigate('/signup')}
                  className="relative px-5 py-2.5 rounded-xl text-sm bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white font-bold transition-all duration-300 shadow-[0_4px_20px_rgba(249,115,22,0.5)] hover:shadow-[0_6px_30px_rgba(249,115,22,0.7)] hover:-translate-y-1 hover:scale-105"
                >
                  Sign Up
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className="h-6 w-6 text-gray-700" />
            ) : (
              <Menu className="h-6 w-6 text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t">
            {isAuthenticated ? (
              <div className="flex flex-col gap-3">
                <Link 
                  to="/dashboard" 
                  className="px-4 py-3 rounded-lg border-2 border-red-300 bg-white hover:bg-red-50 text-gray-800 font-semibold transition-all text-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <Link 
                  to="/place-order" 
                  className="px-4 py-3 rounded-lg border-2 border-yellow-300 bg-white hover:bg-yellow-50 text-gray-800 font-semibold transition-all text-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Place Order
                </Link>
                <Link 
                  to="/referral-links" 
                  className="px-4 py-3 rounded-lg border-2 border-pink-300 bg-white hover:bg-pink-50 text-gray-800 font-semibold transition-all text-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Referral Links
                </Link>
                <Link 
                  to="/transactions" 
                  className="px-4 py-3 rounded-lg border-2 border-red-300 bg-white hover:bg-red-50 text-gray-800 font-semibold transition-all text-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Transactions
                </Link>
                <Link 
                  to="/messages" 
                  className="px-4 py-3 rounded-lg border-2 border-yellow-300 bg-white hover:bg-yellow-50 text-gray-800 font-semibold transition-all text-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Messages
                </Link>
                <Link 
                  to="/profile" 
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-pink-300 bg-white hover:bg-pink-50 text-gray-800 font-semibold transition-all"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Settings className="h-4 w-4" />
                  Profile Settings
                </Link>
                <span className="text-gray-700 font-medium py-2 text-center">Hi, {user?.firstName}</span>
                <Button 
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                  variant="outline"
                  className="border-2 border-gray-300 hover:border-red-500 hover:text-red-600"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2.5">
                <Link 
                  to="/" 
                  className="px-5 py-3 rounded-lg text-lg border border-blue-500 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold transition-all text-center shadow-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </Link>
                <Link 
                  to="/about" 
                  className="px-5 py-3 rounded-lg text-lg border border-green-500 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold transition-all text-center shadow-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  About Us
                </Link>
                <Link 
                  to="/coverage-area" 
                  className="px-5 py-3 rounded-lg text-lg border border-purple-500 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-semibold transition-all text-center shadow-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Coverage Area
                </Link>
                <Link 
                  to="/speed-test" 
                  className="px-5 py-3 rounded-lg text-lg border border-orange-500 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold transition-all text-center shadow-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Speed Test
                </Link>
                <Link 
                  to="/make-payment" 
                  className="px-5 py-3 rounded-lg text-lg border border-red-500 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold transition-all text-center shadow-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Make Payment
                </Link>
                <Link 
                  to="/request-service" 
                  className="px-5 py-3 rounded-lg text-lg border border-emerald-500 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold transition-all text-center shadow-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Order Services
                </Link>
                <Link 
                  to="/join-our-team" 
                  className="px-5 py-3 rounded-lg text-lg border border-yellow-500 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white font-semibold transition-all text-center shadow-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Join Our Team
                </Link>
                <Link 
                  to="/career" 
                  className="px-5 py-3 rounded-lg text-lg border border-teal-500 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white font-semibold transition-all text-center shadow-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Career
                </Link>
                
                {/* Customer Portal */}
                <button
                  onClick={() => {
                    navigate('/customer-portal/login');
                    setIsMenuOpen(false);
                  }}
                  data-testid="mobile-nav-customer-portal-link"
                  className="px-5 py-3 rounded-lg text-lg border border-pink-500 bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white font-semibold transition-all text-center flex items-center justify-center gap-2 shadow-md"
                >
                  <User className="h-5 w-5" />
                  Customer Portal
                </button>
                
                {/* Admin Portal */}
                <button
                  onClick={() => {
                    navigate('/Customer-dashboard/login');
                    setIsMenuOpen(false);
                  }}
                  data-testid="mobile-nav-admin-portal-link"
                  className="px-5 py-3 rounded-lg text-lg border border-indigo-500 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white font-semibold transition-all text-center flex items-center justify-center gap-2 shadow-md"
                >
                  <User className="h-5 w-5" />
                  Admin Portal
                </button>
                
                <Button 
                  onClick={() => {
                    navigate('/signup');
                    setIsMenuOpen(false);
                  }}
                  className="text-white font-bold border-2 border-yellow-400"
                  style={{
                    background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)'
                  }}
                >
                  Sign Up
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
